import useMaintenanceService from '../../../hooks/maintenance-services/useMaintenanceService.ts';
import useUpdateMaintenanceServiceMetadata from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceMetadata.ts';
import useUpdateMaintenanceServiceTags from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceTags.ts';
import useEnableMaintenanceService from '../../../hooks/maintenance-services/useEnableMaintenanceService.ts';
import usePauseMaintenanceService from '../../../hooks/maintenance-services/usePauseMaintenanceService.ts';
import useDisableMaintenanceService from '../../../hooks/maintenance-services/useDisableMaintenanceService.ts';
import {
  UpdateMaintenanceServiceMetadataRequest,
  UpdateMaintenanceServiceTagsRequest,
} from '../../../api/requests/maintenance-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateMaintenanceServiceHeaderInfoForm, {
  MutateMaintenanceServiceHeaderInfoFormValues,
} from '../../ui/places/MutateMaintenanceServiceHeaderInfoForm.tsx';

interface UpdateMaintenanceServiceHeaderInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateMaintenanceServiceHeaderInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateMaintenanceServiceHeaderInfoModalProps) => {
  const { data: maintenanceService, isLoading: isMaintenanceServiceLoading } =
    useMaintenanceService(serviceId);

  const { mutateAsync: updateMaintenanceServiceMetadata } =
    useUpdateMaintenanceServiceMetadata();
  const { mutateAsync: updateMaintenanceServiceTags } =
    useUpdateMaintenanceServiceTags();
  const { mutateAsync: enableMaintenanceService } =
    useEnableMaintenanceService();
  const { mutateAsync: pauseMaintenanceService } = usePauseMaintenanceService();
  const { mutateAsync: disableMaintenanceService } =
    useDisableMaintenanceService();

  const {
    mutate: handleSubmit,
    isPending: isUpdateTagsMetadataAndStatusPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateMaintenanceServiceHeaderInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateMaintenanceServiceMetadataRequest: UpdateMaintenanceServiceMetadataRequest =
        {
          brandId: maintenanceService.brandId,
          name: formValues.name,
          notes: maintenanceService.notes,
          phoneNumber: maintenanceService.phoneNumber,
          pricingSource: maintenanceService.pricingSource,
          serviceId: serviceId,
          website: maintenanceService.website,
        };

      await updateMaintenanceServiceMetadata(
        updateMaintenanceServiceMetadataRequest,
      );

      const updateMaintenanceServiceTagsRequest: UpdateMaintenanceServiceTagsRequest =
        {
          serviceId: serviceId,
          tags: formValues.tags,
        };

      await updateMaintenanceServiceTags(updateMaintenanceServiceTagsRequest);

      if (formValues.serviceStatus === 'ONLINE') {
        await enableMaintenanceService(serviceId);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseMaintenanceService(serviceId);
      } else {
        await disableMaintenanceService(serviceId);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新服務資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯保修資訊"
      centered
      closeOnClickOutside={false}
      size={440}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isMaintenanceServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateMaintenanceServiceHeaderInfoForm
          initialValues={{
            name: maintenanceService.name,
            serviceStatus: maintenanceService.serviceStatus,
            tags: maintenanceService.tags,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateTagsMetadataAndStatusPending}
        />
      )}
    </Modal>
  );
};

export { UpdateMaintenanceServiceHeaderInfoModal };
